import { useEffect, useRef, useState, type PropsWithChildren } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  BellOff,
  BookUser,
  CalendarDays,
  Check,
  Clock3,
  ClipboardList,
  FileCheck2,
  HelpCircle,
  Inbox,
  LayoutDashboard,
  Leaf,
  LogOut,
  MessageCircleMore,
  MessagesSquare,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  PackageSearch,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
  Users
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { hasPermission, roleLabels, type Permission } from "@htzap/contracts";
import { api } from "../lib/api";
import { isNavigationItemActive } from "../lib/navigation";
import { useRealtimeUpdates } from "../lib/realtime";
import type { Overview, TeamChatParticipant } from "../lib/types";
import { Avatar, Button, Modal } from "./ui";
import { GlobalSearch } from "./GlobalSearch";
import { ProcessNotifications } from "./ProcessNotifications";
import { AgendaReminderAlerts } from "./AgendaReminderAlerts";
import { PwaInstallButton, PwaUpdateNotice } from "./PwaControls";
import { initialTheme, type ThemeName } from "../lib/theme";

const nav = [
  { to: "/inbox", label: "Atendimentos", icon: Inbox },
  { to: "/agenda", label: "Agenda", icon: CalendarDays },
  { to: "/stock", label: "Estoque", icon: PackageSearch },
  { to: "/contacts", label: "Contatos", icon: BookUser },
  { to: "/processes", label: "Processos", icon: ClipboardList },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/team-chat", label: "Chat da equipe", icon: MessagesSquare },
  { to: "/team", label: "Equipe", icon: Users },
  { to: "/settings", label: "Configurações", icon: Settings }
] as const;

const mobileNav = [
  { to: "/inbox", label: "Chat", icon: MessageCircleMore },
  { to: "/agenda", label: "Agenda", icon: CalendarDays },
  { to: "/processes", label: "Processos", icon: ClipboardList }
] as const;

const themeOptions = [
  {
    value: "light",
    label: "Claro",
    description: "Superfícies luminosas para ambientes bem iluminados.",
    icon: Sun
  },
  {
    value: "dark",
    label: "Escuro clássico",
    description: "Grafite confortável com os detalhes verdes originais.",
    icon: Moon
  },
  {
    value: "comfort",
    label: "Conforto",
    description: "Grafite quente, verde sálvia e brilho suave.",
    icon: Leaf
  },
  {
    value: "black",
    label: "Black Neon",
    description: "Preto absoluto, mensagens em destaque e atenção amarela.",
    icon: Sparkles
  }
] as const;

export function Shell({ children }: PropsWithChildren) {
  const { user, logout } = useAuth();
  const overview = useQuery({
    queryKey: ["overview"],
    queryFn: () => api<Overview>("/v1/overview?assigned=me")
  });
  const teamChatParticipants = useQuery({
    queryKey: ["team-chat", "participants"],
    queryFn: () =>
      api<TeamChatParticipant[]>("/v1/team-chat/participants")
  });
  const hasUnreadTeamChat = Boolean(
    teamChatParticipants.data?.some((participant) => participant.unreadCount > 0)
  );
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [theme, setTheme] = useState<ThemeName>(initialTheme);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("htzap-sidebar") === "collapsed"
  );
  const [soundEnabled, setSoundEnabled] = useState(
    () => localStorage.getItem("htzap-notification-sound") !== "off"
  );
  const [rulesOpen, setRulesOpen] = useState(false);
  useRealtimeUpdates({ soundEnabled, userId: user?.id });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("htzap-theme", theme);
    localStorage.setItem("htzap-theme-version", "2");
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(
      "htzap-sidebar",
      sidebarCollapsed ? "collapsed" : "expanded"
    );
  }, [sidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem(
      "htzap-notification-sound",
      soundEnabled ? "on" : "off"
    );
  }, [soundEnabled]);

  const navPermissions: Record<(typeof nav)[number]["to"], Permission> = {
    "/inbox": "chat.use", "/agenda": "agenda.manage", "/stock": "stock.withdraw",
    "/processes": "processes.use",
    "/contacts": "contacts.manage", "/dashboard": "dashboard.view",
    "/team-chat": "chat.use", "/team": "team.view", "/settings": "team.view"
  };
  const visibleNav = nav.filter((item) => hasPermission(user, navPermissions[item.to]));
  const primaryNav = visibleNav.filter((item) =>
    ["/inbox", "/agenda", "/stock", "/contacts", "/processes", "/team-chat"].includes(
      item.to
    )
  );
  const secondaryNav = visibleNav.filter((item) =>
    ["/dashboard", "/team"].includes(item.to)
  );
  const settingsNav = visibleNav.find((item) => item.to === "/settings");

  const renderNavItem = (
    item: (typeof nav)[number],
    className = ""
  ) => {
    const active = isNavigationItemActive(pathname, item.to);
    return (
      <Link
        to={item.to}
        hash={item.to === "/settings" && !hasPermission(user, "admin.users") ? "companies" : undefined}
        key={item.to}
        className={`nav-item ${className} ${active ? "active" : ""}`.trim()}
        title={!hasPermission(user, navPermissions[item.to]) ? "É necessário acesso de Técnico Admin" : sidebarCollapsed ? item.label : undefined}
        aria-label={
          item.to === "/team-chat" && hasUnreadTeamChat
            ? "Chat da equipe, novas mensagens"
            : undefined
        }
      >
        <item.icon size={19} />
        <span>{item.label}</span>
        {item.to === "/inbox" && (overview.data?.tickets.unread || 0) > 0 ? (
          <i className="nav-count">{overview.data?.tickets.unread}</i>
        ) : null}
        {item.to === "/team-chat" && hasUnreadTeamChat ? (
          <i className="nav-unread-dot" aria-hidden="true" />
        ) : null}
      </Link>
    );
  };

  return (
    <div
      className={`app-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}
    >
      <AgendaReminderAlerts userId={user?.id} />
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">
            <MessageCircleMore size={25} strokeWidth={2.4} />
          </span>
          <div>
            <strong>HTZAP</strong>
            <small>OFICIAL</small>
          </div>
        </div>
        <button
          className="sidebar-toggle"
          type="button"
          aria-label={
            sidebarCollapsed ? "Expandir menu lateral" : "Recolher menu lateral"
          }
          aria-expanded={!sidebarCollapsed}
          title={
            sidebarCollapsed ? "Expandir menu lateral" : "Recolher menu lateral"
          }
          onClick={() => setSidebarCollapsed((value) => !value)}
        >
          {sidebarCollapsed ? (
            <PanelLeftOpen size={17} />
          ) : (
            <PanelLeftClose size={17} />
          )}
        </button>
        <nav className="main-nav" aria-label="Navegação principal">
          <div className="nav-group nav-group-primary">
            {primaryNav.map((item) => renderNavItem(item))}
          </div>
          <div className="nav-group nav-group-secondary">
            {secondaryNav.map((item) => renderNavItem(item, "nav-item-secondary"))}
          </div>
        </nav>
        <div className="sidebar-bottom">
          {settingsNav ? renderNavItem(settingsNav, "sidebar-settings-link") : null}
          <button className="profile-card" onClick={() => void 0}>
            <Avatar name={user?.name || "Usuário"} size="sm" />
            <span>
              <strong>{user?.name}</strong>
              <small>
                {user ? roleLabels[user.role] : "Técnico"}
              </small>
            </span>
            <i>•••</i>
          </button>
          <Button
            variant="ghost"
            size="sm"
            className="logout-button"
            onClick={() => void logout()}
          >
            <LogOut size={16} /> <span>Sair</span>
          </Button>
        </div>
      </aside>

      <div className="app-surface">
        <header className="topbar">
          <GlobalSearch />
          <div className="top-actions">
            <ProcessNotifications />
            <PwaInstallButton />
            <button
              className="icon-button"
              type="button"
              onClick={() => setRulesOpen(true)}
              aria-label="Entender as regras do WhatsApp Oficial"
              title="Regras do WhatsApp Oficial"
            >
              <HelpCircle size={18} />
            </button>
            <ThemePicker theme={theme} onChange={setTheme} />
            <button
              className={`icon-button notification ${
                soundEnabled ? "sound-on" : "sound-off"
              }`}
              aria-label={
                soundEnabled
                  ? "Desativar som das notificações"
                  : "Ativar som das notificações"
              }
              title={
                soundEnabled
                  ? "Som de novas mensagens ativado"
                  : "Som de novas mensagens desativado"
              }
              aria-pressed={soundEnabled}
              onClick={() => setSoundEnabled((value) => !value)}
            >
              {soundEnabled ? <Bell size={18} /> : <BellOff size={18} />}
              {(overview.data?.tickets.unread || 0) > 0 || hasUnreadTeamChat ? (
                <i />
              ) : null}
            </button>
            <span className="top-divider" />
            <Avatar name={user?.name || "Usuário"} size="sm" />
          </div>
        </header>
        <main className="page">{children}</main>
      </div>

      <header className="mobile-topbar">
        <div className="mobile-brand">
          <span className="brand-mark">
            <MessageCircleMore size={21} strokeWidth={2.4} />
          </span>
          <span>
            <strong>HTZAP</strong>
            <small>OFICIAL</small>
          </span>
        </div>
        <div className="mobile-actions">
          <ProcessNotifications />
          <GlobalSearch compact registerShortcut={false} />
          <PwaInstallButton />
          <button
            className="icon-button"
            type="button"
            onClick={() => setRulesOpen(true)}
            aria-label="Entender as regras do WhatsApp Oficial"
          >
            <HelpCircle size={18} />
          </button>
          <ThemePicker theme={theme} onChange={setTheme} />
          <button
            className={`icon-button notification ${
              soundEnabled ? "sound-on" : "sound-off"
            }`}
            aria-label={
              soundEnabled
                ? "Desativar som das notificações"
                : "Ativar som das notificações"
            }
            aria-pressed={soundEnabled}
            onClick={() => setSoundEnabled((value) => !value)}
          >
            {soundEnabled ? <Bell size={18} /> : <BellOff size={18} />}
            {(overview.data?.tickets.unread || 0) > 0 || hasUnreadTeamChat ? (
              <i />
            ) : null}
          </button>
          <Avatar name={user?.name || "Usuário"} size="sm" />
        </div>
      </header>

      <nav className="mobile-nav" aria-label="Navegação móvel">
        {mobileNav.map((item) => (
          <Link
            to={item.to}
            key={item.to}
            className={pathname.startsWith(item.to) ? "active" : ""}
            aria-label={item.to === "/inbox" && (overview.data?.tickets.unread || 0) > 0 ? "Chat, novas mensagens" : undefined}
          >
            <item.icon size={20} />
            <span>{item.label}</span>
            {item.to === "/inbox" && (overview.data?.tickets.unread || 0) > 0 ? (
              <i className="nav-unread-dot" aria-hidden="true" />
            ) : null}
          </Link>
        ))}
        <ThemePicker theme={theme} onChange={setTheme} showLabel />
      </nav>
      <PwaUpdateNotice />

      <WhatsAppRulesModal
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
      />
    </div>
  );
}

function ThemePicker({
  theme,
  onChange,
  showLabel = false
}: {
  theme: ThemeName;
  onChange: (theme: ThemeName) => void;
  showLabel?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const activeOption = themeOptions.find((option) => option.value === theme)!;
  const ActiveIcon = activeOption.icon;

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div className="theme-picker" ref={rootRef}>
      <button
        className={`icon-button theme-trigger ${open ? "active" : ""}`}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label="Escolher tema visual"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`Tema: ${activeOption.label}`}
      >
        <ActiveIcon size={18} />
        {showLabel ? <span>Aparência</span> : null}
      </button>
      {open ? (
        <div className="theme-menu" role="listbox" aria-label="Temas visuais">
          <div className="theme-menu-heading">
            <strong>Aparência</strong>
            <small>Escolha o contraste que combina com seu ambiente.</small>
          </div>
          <div className="theme-options">
            {themeOptions.map((option) => {
              const OptionIcon = option.icon;
              const selected = option.value === theme;
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`theme-option theme-option-${option.value} ${
                    selected ? "selected" : ""
                  }`}
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <span className="theme-option-icon">
                    <OptionIcon size={18} />
                  </span>
                  <span className="theme-option-copy">
                    <strong>{option.label}</strong>
                    <small>{option.description}</small>
                  </span>
                  <span className="theme-option-choice" aria-hidden="true">
                    {selected ? <Check size={15} /> : <i />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function WhatsAppRulesModal({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Regras do WhatsApp Oficial"
      eyebrow="Guia rápido"
      width="660px"
    >
      <div className="rules-help-intro">
        O HTZAP aplica estas regras automaticamente para proteger a conta da
        empresa e deixar claro o que pode ser enviado em cada momento.
      </div>
      <div className="rules-help-grid">
        <article className="rule-help-card">
          <span><Clock3 size={19} /></span>
          <div>
            <strong>Janela de 24 horas</strong>
            <p>
              Começa ou reinicia quando o cliente envia uma mensagem. Dentro
              dela, a equipe pode responder livremente; mensagens enviadas pela
              empresa não reiniciam o prazo.
            </p>
          </div>
        </article>
        <article className="rule-help-card">
          <span><MessageCircleMore size={19} /></span>
          <div>
            <strong>Iniciar ou retomar conversa</strong>
            <p>
              Fora da janela, a primeira mensagem precisa usar um template
              aprovado pela Meta. A resposta do cliente abre uma nova janela.
            </p>
          </div>
        </article>
        <article className="rule-help-card">
          <span><ShieldCheck size={19} /></span>
          <div>
            <strong>Consentimento</strong>
            <p>
              Registra quando, como e para qual finalidade o cliente autorizou
              o contato. Ele não abre a janela de 24 horas e não substitui o
              template.
            </p>
          </div>
        </article>
        <article className="rule-help-card">
          <span><FileCheck2 size={19} /></span>
          <div>
            <strong>Templates e status</strong>
            <p>
              Templates passam pela análise da Meta. Envio, entrega, leitura e
              falha são estados diferentes e são atualizados pelos webhooks.
            </p>
          </div>
        </article>
      </div>
      <div className="rule-help-note">
        Em dúvida, procure o selo da janela acima do campo de mensagem: o HTZAP
        bloqueia automaticamente envios livres quando o prazo terminar.
      </div>
      <div className="modal-actions">
        <Button onClick={onClose}>Entendi</Button>
      </div>
    </Modal>
  );
}
